import React, { useEffect, useRef, useState } from "react";
import Drawer from "~/components/Drawer/Drawer";
import { useMediaQuery } from "~/hooks/useMediaQuery";
import CloseIcon from "~/icons/CloseIcon";
import styles from "./ChooseTimeSheet.module.css";

type Props = {
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
};

const DEFAULT_TITLE = "When would you like it?";
// Exit feels quicker than the entrance.
const EXIT_MS = 180;

/**
 * Bottom sheet on mobile (Drawer), centered dialog on desktop. Modeled on the
 * store-finder shell so the time picker feels like the same family. Modals stay
 * centered (transform-origin: center) — they aren't anchored to a trigger.
 */
const ChooseTimeSheet: React.FC<Props> = ({
  visible,
  onClose,
  children,
  title = DEFAULT_TITLE,
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const panelRef = useRef<HTMLDivElement>(null);

  const [mounted, setMounted] = useState(visible);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      setClosing(false);
      return;
    }
    if (!mounted) return;
    setClosing(true);
    const t = window.setTimeout(() => setMounted(false), EXIT_MS);
    return () => window.clearTimeout(t);
  }, [visible, mounted]);

  useEffect(() => {
    if (!visible || isMobile) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    panelRef.current
      ?.querySelector<HTMLElement>(
        "button, [tabindex]:not([tabindex='-1'])",
      )
      ?.focus();
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [visible, isMobile, onClose]);

  if (isMobile) {
    return (
      <Drawer visible={visible} onClose={onClose} title={title}>
        <div className={styles.sheet}>{children}</div>
      </Drawer>
    );
  }

  if (!mounted) return null;

  return (
    <div
      className={`${styles.backdrop}${closing ? ` ${styles.closing}` : ""}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        ref={panelRef}
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
          <button
            type="button"
            className={styles.close}
            aria-label="Close"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>
        <div className={styles.body}>{children}</div>
      </div>
    </div>
  );
};

export default ChooseTimeSheet;
